// ===============================================================
// ProBank — App Root
//
// Routing, layout, country guard, session security and global
// error handling. Public pages (login, register, payment-success,
// download) are never interrupted by the auto-logout guard.
//
// TransactionToast is mounted globally — whenever a transaction
// completes (any page), a success banner drops down with a chime.
//
// BottomNav is rendered for logged-in users on app pages (hidden
// on public pages, admin pages, and full-screen wallet flows).
// ===============================================================

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { useState, useEffect, Suspense } from "react";

// ---------- Context & Utilities ----------
import { CountryProvider, useCountry } from "./context/CountryContext";
import { subscribeToPush } from "./utils/push";

// ---------- Components ----------
import Navbar from "./components/Navbar";
import AdminRoute from "./components/AdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingOverlay from "./components/LoadingOverlay";
import TransactionToast from "./components/TransactionToast";
import BottomNav from "./components/BottomNav";

// ---------- Pages ----------
import CountrySelectorPage from "./pages/CountrySelectorPage";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import DownloadPage from "./pages/DownloadPage";
import Dashboard from "./pages/Dashboard";
import DashboardRouter from "./pages/DashboardRouter";
import BasicDashboard from "./pages/BasicDashboard";
import PremiumDashboard from "./pages/PremiumDashboard";
import EliteDashboard from "./pages/EliteDashboard";
import WalletPage from "./pages/WalletPage";
import WalletPalmPayPage from "./pages/WalletPalmPayPage";
import WalletKudaPage from "./pages/WalletKudaPage";
import TransferToOpayPage from "./pages/TransferToOpayPage";
import TransferToBankPage from "./pages/TransferToBankPage";
import TransferSelect from "./pages/TransferSelect";
import POSTransferPage from "./pages/POSTransferPage";
import LoanPaymentPage from "./pages/LoanPaymentPage";
import InternationalTransferPage from "./pages/InternationalTransferPage";
import KudaSendToBankPage from "./pages/KudaSendToBankPage";
import PalmPaySendBankPage from "./pages/PalmPaySendBankPage";
import PalmPaySendPalmPayPage from "./pages/PalmPaySendPalmPayPage";
import VendorPage from "./pages/VendorPage";
import VendorPurchasePage from "./pages/VendorPurchasePage";
import VendorOrdersPage from "./pages/VendorOrdersPage";
import PurchasePage from "./pages/PurchasePage";
import BecomeVendor from "./pages/BecomeVendor";
import DescriptionPage from "./pages/DescriptionPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionPaymentPage from "./pages/SubscriptionPaymentPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import UserChatPage from "./pages/UserChatPage";
import OpayHistoryPage from "./pages/OpayHistoryPage";
import TransactionDetailsPage from "./pages/TransactionDetailsPage";
import LunoDashboard from "./pages/lunodashboard";
import MoniepointPage from "./pages/MoniepointPage";
import Withdrawals from "./pages/Withdrawals";
import AboutUs from "./pages/AboutUs";
import CardsPage from "./pages/CardsPage";
import CardApplyPage from "./pages/CardApplyPage";
import ProfilePage from "./pages/ProfilePage";

// ---------- Admin Pages ----------
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOverview from "./pages/AdminOverview";
import AdminUsers from "./pages/AdminUsers";
import AdminVendors from "./pages/AdminVendors";
import AdminCredit from "./pages/AdminCredit";
import AdminChatPage from "./pages/AdminChatPage";
import AdminVendorPurchases from "./pages/AdminVendorPurchases";
import AdminSendEmail from "./pages/AdminSendEmail";
import AdminSendNotification from "./pages/AdminSendNotification";
import AdminTickets from "./pages/AdminTickets";
import BroadcastEmail from "./pages/BroadcastEmail";
import AdminCards from "./pages/AdminCards";

// ---------------------------------------------------------------
// Session security configuration
// ---------------------------------------------------------------

const PUBLIC_PATHS = [
  "/",
  "/home",
  "/login",
  "/register",
  "/admin-login",
  "/payment-success",
  "/about-us",
  "/download",
];

const AWAY_LIMIT_MS = 15 * 60 * 1000;

const LEFT_AT_KEY = "leftAppAt";

// ---------------------------------------------------------------
// Country Guard — routes that require a country selection
// ---------------------------------------------------------------

function CountryGuard({ children }) {
  let hasSelected = false;

  try {
    const ctx = useCountry();
    hasSelected = ctx?.hasSelected ?? false;
  } catch (err) {
    console.warn("Country context error, falling back to localStorage:", err);
    hasSelected = !!localStorage.getItem("userCountry");
  }

  const location = useLocation();

  const allowedWithoutCountry = ["/", "/admin-login", "/payment-success", "/download"];

  if (!hasSelected && !allowedWithoutCountry.includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ---------------------------------------------------------------
// Layout — navbar visibility, bottom nav and route loader
// ---------------------------------------------------------------

function Layout({ children }) {
  const location = useLocation();
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    setRouteLoading(true);
    const timer = setTimeout(() => setRouteLoading(false), 800);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const hideNavbarRoutes = [
    "/",
    "/home",
    "/download",
    "/moniepoint",
    "/opay",
    "/pampay",
    "/Kuda",
    "/luno-dashboard",
    "/transfer-opay",
    "/transfer-bank",
    "/transfer-select",
    "/transfer/pos",
    "/transfer/bank",
    "/transfer/loan",
    "/transfer/international",
    "/send-bank",
    "/send-palmpay",
    "/wallet",
    "/wallet-kuda",
    "/wallet-palmpay",
    "/opay/history",
    "/login",
    "/register",
    "/payment",
    "/subscription-payment",
    "/payment-success",
    /^\/transaction\/.+$/,
  ];

  const shouldHideNavbar = hideNavbarRoutes.some((route) => {
    try {
      if (typeof route === "string") return route === location.pathname;
      if (route instanceof RegExp) return route.test(location.pathname);
    } catch (err) {
      console.warn("Route check error:", err);
    }
    return false;
  });

  // Bottom navigation — only for logged-in users on regular app
  // pages. "/cards" renders its own BottomNav internally.
  const isAdminPath = location.pathname.startsWith("/admin");
  const isLoggedIn = !!localStorage.getItem("token");
  const showBottomNav =
    isLoggedIn &&
    !isAdminPath &&
    !shouldHideNavbar &&
    location.pathname !== "/cards";

  return (
    <>
      {routeLoading && <LoadingOverlay />}
      {!shouldHideNavbar && <Navbar />}
      <TransactionToast />
      <div
        className={
          (!shouldHideNavbar ? "pt-16" : "") +
          (showBottomNav ? " pb-24" : "")
        }
      >
        {children}
      </div>
      {showBottomNav && <BottomNav />}
    </>
  );
}

// ---------------------------------------------------------------
// Crash fallback UI
// ---------------------------------------------------------------

function CrashFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#020617] via-[#071032] to-black text-white p-6">
      <div className="max-w-md text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-slate-400 text-sm mb-6">
          The app encountered an unexpected error. Don't worry — your data is safe.
        </p>
        <button
          onClick={() => {
            try {
              localStorage.removeItem("userCountry");
            } catch (e) {}
            window.location.href = "/";
          }}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 text-white font-semibold shadow-lg"
        >
          Reload App
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// App
// ---------------------------------------------------------------

function App() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [appError, setAppError] = useState(false);

  useEffect(() => {
    try {
      const timer = setTimeout(() => setInitialLoading(false), 2000);
      return () => clearTimeout(timer);
    } catch (err) {
      console.error("Initial load error:", err);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");

      let userId = localStorage.getItem("userId");
      if (!userId) {
        const userObj = JSON.parse(localStorage.getItem("user") || "{}");
        userId = userObj._id || userObj.id || userObj.userId || null;
      }

      if (token && userId) {
        setTimeout(() => {
          subscribeToPush(userId);
        }, 3000);
      }
    } catch (err) {
      console.warn("Push subscribe skipped:", err);
    }
  }, []);

  useEffect(() => {
    const forceLogout = () => {
      try {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        localStorage.removeItem(LEFT_AT_KEY);
        window.location.href = "/login";
      } catch (err) {
        console.warn("Auto-logout error:", err);
      }
    };

    const handleVisibilityChange = () => {
      try {
        const path = window.location.pathname;

        if (PUBLIC_PATHS.includes(path)) {
          localStorage.removeItem(LEFT_AT_KEY);
          return;
        }

        const hasSession = !!localStorage.getItem("token");
        if (!hasSession) {
          localStorage.removeItem(LEFT_AT_KEY);
          return;
        }

        if (document.visibilityState === "hidden") {
          localStorage.setItem(LEFT_AT_KEY, Date.now().toString());
        } else if (document.visibilityState === "visible") {
          const leftAt = parseInt(localStorage.getItem(LEFT_AT_KEY) || "0", 10);

          if (leftAt) {
            const awayMs = Date.now() - leftAt;
            localStorage.removeItem(LEFT_AT_KEY);

            if (awayMs >= AWAY_LIMIT_MS) {
              forceLogout();
            }
          }
        }
      } catch (err) {
        console.warn("Visibility handler error:", err);
      }
    };

    handleVisibilityChange();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleError = (event) => {
      console.error("Global error:", event.error || event.reason);
    };
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleError);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleError);
    };
  }, []);

  if (appError) {
    return <CrashFallback />;
  }

  if (initialLoading) {
    return <LoadingOverlay />;
  }

  return (
    <ErrorBoundary fallback={<CrashFallback />}>
      <CountryProvider>
        <Router>
          <Layout>
            <ErrorBoundary fallback={<CrashFallback />}>
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#020617] via-[#071032] to-black">
                    <p className="text-slate-300 text-lg animate-pulse">Loading...</p>
                  </div>
                }
              >
                <Routes>
                  {/* First page: country selector (public) */}
                  <Route path="/" element={<CountrySelectorPage />} />
                  <Route path="/home" element={<CountryGuard><LandingPage /></CountryGuard>} />

                  {/* Public */}
                  <Route path="/login" element={<CountryGuard><Login /></CountryGuard>} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route path="/register" element={<CountryGuard><Register /></CountryGuard>} />
                  <Route path="/about-us" element={<CountryGuard><AboutUs /></CountryGuard>} />
                  <Route path="/download" element={<DownloadPage />} />

                  {/* Payment result — always public, never guarded */}
                  <Route path="/payment-success" element={<PaymentSuccess />} />

                  {/* Dashboards */}
                  <Route path="/dashboard" element={<CountryGuard><DashboardRouter /></CountryGuard>} />
                  <Route path="/dashboard-demo" element={<CountryGuard><Dashboard /></CountryGuard>} />
                  <Route path="/dashboard-basic" element={<CountryGuard><BasicDashboard /></CountryGuard>} />
                  <Route path="/dashboard-premium" element={<CountryGuard><PremiumDashboard /></CountryGuard>} />
                  <Route path="/dashboard-elite" element={<CountryGuard><EliteDashboard /></CountryGuard>} />

                  {/* Virtual card, history & profile (bottom nav) */}
                  <Route path="/cards" element={<CountryGuard><CardsPage /></CountryGuard>} />
                  <Route path="/cards/apply" element={<CountryGuard><CardApplyPage /></CountryGuard>} />
                  <Route path="/history" element={<CountryGuard><OpayHistoryPage /></CountryGuard>} />
                  <Route path="/profile" element={<CountryGuard><ProfilePage /></CountryGuard>} />

                  {/* Payments */}
                  <Route path="/payment" element={<CountryGuard><PaymentPage /></CountryGuard>} />
                  <Route path="/subscription-payment" element={<CountryGuard><SubscriptionPaymentPage /></CountryGuard>} />

                  {/* Wallets & transfers */}
                  <Route path="/luno-dashboard" element={<CountryGuard><LunoDashboard /></CountryGuard>} />
                  <Route path="/wallet" element={<CountryGuard><WalletPage /></CountryGuard>} />
                  <Route path="/wallet-kuda" element={<CountryGuard><WalletKudaPage /></CountryGuard>} />
                  <Route path="/wallet-palmpay" element={<CountryGuard><WalletPalmPayPage /></CountryGuard>} />
                  <Route path="/moniepoint" element={<CountryGuard><MoniepointPage /></CountryGuard>} />
                  <Route path="/transfer-select" element={<CountryGuard><TransferSelect /></CountryGuard>} />
                  <Route path="/transfer-opay" element={<CountryGuard><TransferToOpayPage /></CountryGuard>} />
                  <Route path="/transfer-bank" element={<CountryGuard><TransferToBankPage /></CountryGuard>} />
                  <Route path="/kuda-send-bank" element={<CountryGuard><KudaSendToBankPage /></CountryGuard>} />
                  <Route path="/palmpay/send-bank" element={<CountryGuard><PalmPaySendBankPage /></CountryGuard>} />
                  <Route path="/palmpay/send-palmpay" element={<CountryGuard><PalmPaySendPalmPayPage /></CountryGuard>} />

                  {/* New transfer flows — selector → dedicated pages */}
                  <Route path="/transfer/pos" element={<CountryGuard><POSTransferPage /></CountryGuard>} />
                  <Route path="/transfer/bank" element={<CountryGuard><TransferToBankPage /></CountryGuard>} />
                  <Route path="/transfer/loan" element={<CountryGuard><LoanPaymentPage /></CountryGuard>} />
                  <Route path="/transfer/international" element={<CountryGuard><InternationalTransferPage /></CountryGuard>} />

                  {/* Vendors & purchases */}
                  <Route path="/vendor" element={<CountryGuard><VendorPage /></CountryGuard>} />
                  <Route path="/vendor/orders" element={<CountryGuard><VendorOrdersPage /></CountryGuard>} />
                  <Route path="/vendor/purchase" element={<CountryGuard><VendorPurchasePage /></CountryGuard>} />
                  <Route path="/purchase" element={<CountryGuard><PurchasePage /></CountryGuard>} />
                  <Route path="/become-vendor" element={<CountryGuard><BecomeVendor /></CountryGuard>} />

                  {/* Misc */}
                  <Route path="/description" element={<CountryGuard><DescriptionPage /></CountryGuard>} />
                  <Route path="/subscription" element={<CountryGuard><SubscriptionPage /></CountryGuard>} />
                  <Route path="/support" element={<CountryGuard><UserChatPage /></CountryGuard>} />
                  <Route path="/opay/history" element={<CountryGuard><OpayHistoryPage /></CountryGuard>} />
                  <Route path="/transaction/:id" element={<CountryGuard><TransactionDetailsPage /></CountryGuard>} />
                  <Route path="/admin/broadcast" element={<BroadcastEmail />} />

                  {/* Admin (protected by AdminRoute, no country guard) */}
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    }
                  >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<AdminOverview />} />
                    <Route path="credit" element={<AdminCredit />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="vendors" element={<AdminVendors />} />
                    <Route path="chat" element={<AdminChatPage />} />
                    <Route path="vendor-purchases" element={<AdminVendorPurchases />} />
                    <Route path="send-email" element={<AdminSendEmail />} />
                    <Route path="send-notification" element={<AdminSendNotification />} />
                    <Route path="tickets" element={<AdminTickets />} />
                    <Route path="withdrawals" element={<Withdrawals />} />
                    <Route path="cards" element={<AdminCards />} />
                  </Route>

                  {/* 404 — back to country selector */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </Router>
      </CountryProvider>
    </ErrorBoundary>
  );
}

export default App;
